import { useEffect, useId, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Check, ExternalLink, Lock, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Header } from "../components/Header";
import { URLS } from "../constans";
import { LawyerAPI } from "../api/lawyerApi";
import { lawyerSelectors } from "../redux/lawyer/selectors";
import {
  ONBOARDING_PATH,
  shouldRedirectToOnboarding
} from "../features/onboarding/routes";

/** Posted by the landing site's embed route (caseblink-landing AgreementEndSignal). */
const AGREEMENT_MESSAGE = {
  ready: "caseblink:service-agreement:ready",
  end: "caseblink:service-agreement:end"
} as const;

const AGREEMENT_LOAD_TIMEOUT_MS = 8000;

/** caseblink.com redirects to www, so the frame can answer from either host. */
const AGREEMENT_ORIGINS = (() => {
  const { protocol, host } = new URL(URLS.serviceAgreementEmbed);
  const bare = host.replace(/^www\./, "");
  return new Set([`${protocol}//${bare}`, `${protocol}//www.${bare}`]);
})();

const AGREEMENT_LABEL = (() => {
  const { host, pathname } = new URL(URLS.serviceAgreement);
  return `${host}${pathname}`;
})();

type FrameState = "loading" | "ready" | "failed";

function messageType(data: unknown): unknown {
  return typeof data === "object" && data !== null && "type" in data
    ? data.type
    : undefined;
}

function AcceptAgreement() {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [frame, setFrame] = useState<FrameState>("loading");
  const [readToEnd, setReadToEnd] = useState(false);
  const [opened, setOpened] = useState(false);
  const uid = useSelector(lawyerSelectors.selectUid);
  const onboardingStatus = useSelector(lawyerSelectors.selectOnboardingStatus);
  const navigate = useNavigate();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const agreeId = useId();

  const canAgree = frame === "failed" ? opened : readToEnd;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      if (!AGREEMENT_ORIGINS.has(event.origin)) return;
      const type = messageType(event.data);
      if (type === AGREEMENT_MESSAGE.ready) {
        setFrame((current) => (current === "loading" ? "ready" : current));
      } else if (type === AGREEMENT_MESSAGE.end) {
        setFrame((current) => (current === "loading" ? "ready" : current));
        setReadToEnd(true);
      }
    };
    window.addEventListener("message", onMessage);
    const timeout = window.setTimeout(() => {
      setFrame((current) => (current === "loading" ? "failed" : current));
    }, AGREEMENT_LOAD_TIMEOUT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timeout);
    };
  }, []);

  const handleAccept = async () => {
    const lawyerId = String(uid || "").trim();
    if (!lawyerId) return;
    await LawyerAPI.acceptTerms(lawyerId);
    navigate(
      shouldRedirectToOnboarding(onboardingStatus) ? ONBOARDING_PATH : "/cases"
    );
  };

  const openInNewTab = (variant: "ghost" | "secondary", size: "xs" | "sm") => (
    <Button asChild variant={variant} size={size}>
      <a
        href={URLS.serviceAgreement}
        target="_blank"
        rel="noreferrer"
        onClick={() => setOpened(true)}
      >
        <ExternalLink />
        Open in New Tab
      </a>
    </Button>
  );

  return (
    <div className="flex h-screen w-full flex-col bg-surface">
      <Header />
      <div className="min-h-0 w-full flex-1 overflow-hidden">
        <div className="mx-auto flex h-full min-h-0 w-full max-w-256 flex-col items-center gap-8 px-8 pt-4 pb-6 mobile:gap-6 mobile:px-4 mobile:py-4">
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-center text-xl font-medium text-fg">
              Review Service Agreement
            </h1>
            <p className="text-center text-base text-fg-3">
              Please scroll to the bottom, read, and accept the service
              agreement below to continue using the platform.
            </p>
          </div>

          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl bg-elevated ring-1 ring-line-2">
            <div className="flex h-9 shrink-0 items-center gap-1.5 border-b border-line-1 pr-1.5 pl-3">
              <Lock className="size-3 shrink-0 text-fg-3" aria-hidden />
              <span className="min-w-0 flex-1 truncate font-mono text-sm text-fg-3">
                {AGREEMENT_LABEL}
              </span>
              {frame !== "failed" && readToEnd ? (
                <span className="mr-1 flex shrink-0 items-center gap-1 text-sm text-fg-3">
                  <Check className="size-3 text-success" aria-hidden />
                  Read to the End
                </span>
              ) : null}
              {openInNewTab("ghost", "xs")}
            </div>
            {frame === "failed" ? (
              <EmptyState
                icon={TriangleAlert}
                title="The Agreement Couldn't Load Here"
                description="Open it in a new tab to read it. You can accept it here once it's open."
                action={openInNewTab("secondary", "sm")}
              >
                {opened ? (
                  <span className="text-sm text-fg-3">
                    Opened in a new tab.
                  </span>
                ) : null}
              </EmptyState>
            ) : (
              <div className="relative flex min-h-0 flex-1">
                <iframe
                  ref={frameRef}
                  src={URLS.serviceAgreementEmbed}
                  title="Service Agreement"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                  referrerPolicy="strict-origin"
                  onError={() => setFrame("failed")}
                  className="block min-h-0 w-full flex-1"
                />
                {frame === "loading" ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-elevated">
                    <Spinner className="text-fg-3" />
                  </div>
                ) : null}
              </div>
            )}
          </div>

          <div className="flex w-full flex-col items-center gap-4">
            <Label
              htmlFor={agreeId}
              className={cn(
                "flex items-center gap-2 text-base leading-normal font-normal",
                canAgree ? "text-fg" : "text-fg-3"
              )}
            >
              <Checkbox
                id={agreeId}
                checked={isChecked}
                onCheckedChange={(checked) => setIsChecked(checked === true)}
                disabled={!canAgree}
              />
              I agree to the terms and conditions as set out by the service
              agreement.
            </Label>
            <Button
              className="w-40"
              variant="primary"
              size="md"
              disabled={!isChecked || !canAgree}
              onClick={handleAccept}
            >
              I Accept
            </Button>
            {!canAgree ? (
              <p className="text-sm text-fg-3">
                {frame === "failed"
                  ? "Open the agreement to accept it."
                  : "Scroll to the end of the agreement to accept it."}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptAgreement;
